import { isScreenerPath } from '@upright/ui/common/generic/components/navigation/page-paths';
import { useIsClientOrTeamOrFreeTrial } from '@upright/ui/common/generic/hooks';
import { useShouldShowHomeViewFrame } from '@upright/ui/views/root/main/routes/use-should-show-home-view-frame';
import { useLocation } from 'react-router-dom';
import { isChatSurfacePath } from './unified-shell-chat-routes';
import { useUnifiedShell } from './use-unified-shell';

/**
 * Whether the current route renders inside the unified shell's frame — the white top bar,
 * the rail and the content column of `UnifiedShellFrame` — rather than the legacy home
 * frame under the dark header.
 *
 * Two conditions, both required: the user is in the unified shell (`useUnifiedShell`), and
 * the route is one the frame wraps. Those are the home-view routes (`/`, favourites, custom
 * companies, manage access) and the chat surface (`/chat`, a conversation under it, and the
 * full conversation list at `/chats`) — chat is a view inside this shell rather than a shell
 * of its own (PLT-6610), plus the company, fund and fund-holdings screeners under `/screen`.
 * The entity and internal views keep `BouPageWithFrame` until the shell reaches them
 * (PLT-6734 and its siblings).
 *
 * **This is not the question "which top bar".** An opted-in user gets the shell's white bar on
 * every route, including the ones this hook declines, and `useUnifiedShell` alone decides that
 * (PLT-6767). The two were one condition until the seam it produced — the dark bar one click off
 * the home views — turned out to be the thing readers noticed. So a route this hook returns false
 * for still carries the white bar, over the legacy frame, and `UnifiedTopBarOverLegacyFrame` is
 * what pins it there.
 *
 * Both the header switch (`root/main/header-maybe.tsx`) and the route tree
 * (`root/main/routes/index.tsx`) read this, so the frame cannot paint its own bar under
 * a second one, and no bar can appear over a page that expects the frame's.
 *
 * `loading` is the privilege query still in flight for a customer, and only on a route the frame
 * would wrap: off those routes it reports no wait, because the frame's answer is already settled.
 * A caller that also needs to know which bar to paint has to wait on `useUnifiedShell` itself.
 */
export function useUnifiedShellFrameActive(): { active: boolean; loading: boolean } {
  const showLoggedInHomeView = useIsClientOrTeamOrFreeTrial();
  const onHomeViewRoute = useShouldShowHomeViewFrame(showLoggedInHomeView);
  const { pathname } = useLocation();
  const shell = useUnifiedShell();

  if (!onHomeViewRoute && !isChatSurfacePath(pathname) && !isScreenerPath(pathname)) {
    return { active: false, loading: false };
  }
  return { active: shell.enabled, loading: shell.loading };
}
